/*
  Copyright 2017 Esri
  Licensed under the Apache License, Version 2.0 (the "License");
  you may not use this file except in compliance with the License.
  You may obtain a copy of the License at
    http://www.apache.org/licenses/LICENSE-2.0
  Unless required by applicable law or agreed to in writing, software
  distributed under the License is distributed on an "AS IS" BASIS,
  WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
  See the License for the specific language governing permissions and
  limitations under the License.​
*/
define(["require", "exports", "tslib", "./utilites/errorUtils", "./utilites/esriWidgetUtils", "./utilites/lookupLayerUtils", "esri/core/watchUtils", "./application-base-js/support/domHelper", "./ConfigurationSettings", "./components/DetailPanel", "./components/DisplayLookupResults", "esri/Graphic", "esri/core/Handles", "./components/Header", "./components/Footer", "./components/MapPanel", "esri/widgets/Search", "esri/widgets/Search/SearchSource", "telemetry/telemetry.dojo", "./components/LookupGraphics", "esri/layers/FeatureLayer", "esri/request", "esri/core/Collection", "./utilites/geometryUtils", "./utilites/geometryUtils", "dojo/i18n!./nls/resources", "esri/geometry/Point", "esri/geometry", "esri/widgets/Popup", "esri/symbols", "esri/symbols", "esri/symbols"], function (require, exports, tslib_1, errorUtils_1, esriWidgetUtils_1, lookupLayerUtils_1, watchUtils_1, domHelper_1, ConfigurationSettings_1, DetailPanel_1, DisplayLookupResults_1, Graphic_1, Handles_1, Header_1, Footer_1, MapPanel_1, Search_1, SearchSource_1, telemetry_dojo_1, LookupGraphics, FeatureLayer_1, request_1, Collection_1, geometryUtils_1, geometryUtils_2, i18n, Point_1, geometry_1, Popup_1, symbols_1, symbols_2, symbols_3) {
    "use strict";
    ConfigurationSettings_1 = tslib_1.__importDefault(ConfigurationSettings_1);
    DetailPanel_1 = tslib_1.__importDefault(DetailPanel_1);
    DisplayLookupResults_1 = tslib_1.__importDefault(DisplayLookupResults_1);
    Graphic_1 = tslib_1.__importDefault(Graphic_1);
    Handles_1 = tslib_1.__importDefault(Handles_1);
    Header_1 = tslib_1.__importDefault(Header_1);
    Footer_1 = tslib_1.__importDefault(Footer_1);
    MapPanel_1 = tslib_1.__importDefault(MapPanel_1);
    Search_1 = tslib_1.__importDefault(Search_1);
    SearchSource_1 = tslib_1.__importDefault(SearchSource_1);
    telemetry_dojo_1 = tslib_1.__importDefault(telemetry_dojo_1);
    FeatureLayer_1 = tslib_1.__importDefault(FeatureLayer_1);
    request_1 = tslib_1.__importDefault(request_1);
    Collection_1 = tslib_1.__importDefault(Collection_1);
    Point_1 = tslib_1.__importDefault(Point_1);
    Popup_1 = tslib_1.__importDefault(Popup_1);
    var CSS = {
        loading: 'configurable-application--loading'
    };
    var LocationApp = /** @class */ (function () {
        function LocationApp() {
            //--------------------------------------------------------------------------
            //
            //  Properties
            //
            //--------------------------------------------------------------------------
            this._appConfig = null;
            this.telemetry = null;
            this.searchWidget = null;
            this._searchWidgetAddressType = "ADDRESS";
            this.mapPanel = null;
            this._detailPanel = null;
            this._handles = new Handles_1.default();
            //----------------------------------
            //  ApplicationBase
            //----------------------------------
            this.base = null;
            this._results = null;
            //----------------------------------
            //  Custom variables
            //----------------------------------
            this._urlParamsSearch = false;
            this._addressSearchURL = "";
        }
        //--------------------------------------------------------------------------
        //
        //  Public Methods
        //
        //--------------------------------------------------------------------------
        LocationApp.prototype.init = function (base) {
            var _this = this;
            if (!base) {
                console.error('ApplicationBase is not defined');
                return;
            }
            this._updateMapVisibility(base.config);
            domHelper_1.setPageLocale(base.locale);
            domHelper_1.setPageDirection(base.direction);
            this.base = base;
            var config = base.config, results = base.results, portal = base.portal;
            config.helperServices = tslib_1.__assign({}, base.portal.helperServices);
            var webMapItems = results.webMapItems;
            this._createSharedTheme();
            this._appConfig = new ConfigurationSettings_1.default(config);
            this._handles.add(watchUtils_1.init(this._appConfig, ["theme", "applySharedTheme"], function () {
                _this.handleThemeUpdates();
            }), "configuration");
            // Setup Telemetry
            if (config.telemetry) {
                var options = config.telemetry.prod;
                if (portal.customBaseUrl.indexOf('mapsdevext') !== -1) {
                    // use devext credentials
                    options = config.telemetry.devext;
                }
                else if (portal.customBaseUrl.indexOf('mapsqa') !== -1) {
                    // or qa
                    options = config.telemetry.qaext;
                }
                this.telemetry = new telemetry_dojo_1.default({
                    portal: portal,
                    disabled: options.disabled,
                    debug: options.debug,
                    amazon: options.amazon
                });
                this.telemetry.logPageView();
            }
            // Get web map
            var allItems = webMapItems.map(function (item) {
                return item;
            });
            var validWebMapItems = [];
            allItems.forEach(function (response) {
                if (response && response.error) {
                    return;
                }
                validWebMapItems.push(response.value);
            });
            var item = validWebMapItems[0];
            if (!item) {
                var error = 'Could not load an item to display';
                errorUtils_1.displayError({
                    title: 'Error',
                    message: error
                });
                this.telemetry.logError({
                    error: error
                });
                return;
            }
            this.mapPanel = new MapPanel_1.default({
                item: item,
                base: this.base,
                container: 'mapPanel'
            });
            var panelHandle = this.mapPanel.watch('view', function () {
                panelHandle.remove();
                _this.view = _this.mapPanel.view;
                // Watch and update properties that affect the 
                // way results are displayed
                _this._handles.add(watchUtils_1.init(_this._appConfig, ["displayUnmatchedResults", "groupResultsByLayer"], function () {
                    if (_this._results) {
                        _this._displayResults(_this._results);
                    }
                }), "configuration");
                _this._addHeader(item);
                _this._addDetails();
                _this._addFooter();
                _this.view.popup.close();
                document.body.classList.remove(CSS.loading);
                _this._addWidgets();
                _this.view.watch("popup.selectedFeature", function (newValue, oldValue) {
                    var _a, _b, _c, _d, _e, _f, _g, _h;
                    if (!newValue) {
                        return;
                    }
                    var sketchGraphicsLayer = _this.view.map.findLayerById("SketchToolGraphicsLayer");
                    if (((_a = sketchGraphicsLayer === null || sketchGraphicsLayer === void 0 ? void 0 : sketchGraphicsLayer.graphics) === null || _a === void 0 ? void 0 : _a.length) > 0) {
                        if (newValue && oldValue) {
                            var oldGraphic = (_b = sketchGraphicsLayer.graphics) === null || _b === void 0 ? void 0 : _b.find(function (g) { return g.geometry === (oldValue === null || oldValue === void 0 ? void 0 : oldValue.geometry); });
                            if (oldGraphic) {
                                (_c = sketchGraphicsLayer === null || sketchGraphicsLayer === void 0 ? void 0 : sketchGraphicsLayer.graphics) === null || _c === void 0 ? void 0 : _c.remove(oldGraphic);
                            }
                        }
                        var targetGraphic = new Graphic_1.default();
                        targetGraphic = newValue ? newValue : oldValue;
                        if (targetGraphic) {
                            var targetSymbol;
                            if (((_d = targetGraphic.geometry) === null || _d === void 0 ? void 0 : _d.type) == "polygon") {
                                targetSymbol = new symbols_2.SimpleFillSymbol();
                                targetSymbol.outline = new symbols_3.SimpleLineSymbol();
                                _this.view.extent = targetGraphic.geometry.extent;
                                _this.view.popup.location = targetGraphic.geometry.centroid;
                            }
                            else if (((_e = targetGraphic.geometry) === null || _e === void 0 ? void 0 : _e.type) == "point") {
                                targetSymbol = new symbols_1.SimpleMarkerSymbol();
                                _this.view.popup.location = targetGraphic.geometry;
                            }
                            else if (((_f = targetGraphic.geometry) === null || _f === void 0 ? void 0 : _f.type) == "polyline") {
                                targetSymbol = new symbols_3.SimpleLineSymbol();
                            }
                            targetGraphic.symbol = ((_h = (_g = newValue === null || newValue === void 0 ? void 0 : newValue.sourceLayer) === null || _g === void 0 ? void 0 : _g.renderer) === null || _h === void 0 ? void 0 : _h.symbol) || targetSymbol;
                            sketchGraphicsLayer.graphics.add(targetGraphic);
                        }
                    }
                    else if (newValue.extraOption) {
                        _this._generateSearchResults(newValue.extraOption);
                        if (newValue.geometry.type == "polygon") {
                            _this.view.popup.location = newValue.geometry.centroid;
                            // this.view.extent = newValue.geometry.extent;
                        }
                        else {
                            _this.view.popup.location = newValue.geometry;
                            // this.view.goTo({ target: newValue.geometry, zoom: this._appConfig.pointZoomScale });
                        }
                    }
                });
            });
        };
        LocationApp.prototype._addFooter = function () {
            var _this = this;
            var container = document.createElement("div");
            document.getElementById("sidePanel").appendChild(container);
            var footer = new Footer_1.default({
                noMap: this._appConfig.hideMap,
                container: container
            });
            this._handles.add(watchUtils_1.init(this._appConfig, "hideMap", function () {
                _this._updateMapVisibility(_this._appConfig);
            }), "configuration");
            footer.on("button-clicked", function () {
                _this.mapPanel.isMobileView = true;
                _this.view.container.classList.remove('tablet-hide');
                //update the maps describedby item
                document.getElementById('mapDescription').innerHTML = i18n.map.miniMapDescription;
                var mainNodes = document.getElementsByClassName('main-map-content');
                for (var j = 0; j < mainNodes.length; j++) {
                    mainNodes[j].classList.add('hide');
                }
                // if view size increases to greater than tablet close button if not already closed
                var resizeListener = function () {
                    _this.mapPanel.closeMap();
                    window.removeEventListener("resize", resizeListener);
                };
                window.addEventListener("resize", resizeListener);
            });
        };
        LocationApp.prototype._updateMapVisibility = function (config) {
            var hide = config.hideMap;
            var hideMapClass = "no-map";
            var mapClassList = document.body.classList;
            hide ? mapClassList.add(hideMapClass) : mapClassList.remove(hideMapClass);
        };
        LocationApp.prototype._addDetails = function () {
            var appid = this.base.config.appid;
            var _a = this._appConfig, introductionTitle = _a.introductionTitle, introductionContent = _a.introductionContent;
            if (appid === '') {
                if (!introductionTitle)
                    this._appConfig.introductionTitle = i18n.onboarding.title;
                if (!introductionContent)
                    this._appConfig.introductionContent = i18n.onboarding.content;
            }
            this._detailPanel = new DetailPanel_1.default({
                config: this._appConfig,
                view: this.view,
                container: document.getElementById('detailPanel')
            });
        };
        LocationApp.prototype._addHeader = function (item) {
            this._appConfig.title = this._appConfig.title || item.title;
            var headerComponent = new Header_1.default({
                config: this._appConfig,
                container: document.createElement("div")
            });
            var sidePanel = document.getElementById("sidePanel");
            sidePanel.insertBefore(headerComponent.container, sidePanel.firstChild);
        };
        LocationApp.prototype._addWidgets = function () {
            return tslib_1.__awaiter(this, void 0, void 0, function () {
                return tslib_1.__generator(this, function (_a) {
                    // Add esri widgets to the app (legend, home etc)
                    esriWidgetUtils_1.addMapComponents({
                        view: this.view,
                        config: this._appConfig,
                        portal: this.base.portal
                    });
                    this._setupFeatureSearchType();
                    return [2 /*return*/];
                });
            });
        };
        LocationApp.prototype._setupFeatureSearchType = function () {
            return tslib_1.__awaiter(this, void 0, void 0, function () {
                var config, lookupGraphics, configuredLayers, lookupLayers, lookupProps, searchLayer;
                var _this = this;
                return tslib_1.__generator(this, function (_a) {
                    switch (_a.label) {
                        case 0:
                            config = this.base.config;
                            // Determine search lookup type
                            if (!config.enableSearchLayer) {
                                this.base.config.searchLayer = null;
                            }
                            lookupGraphics = new LookupGraphics({
                                view: this.view,
                                config: this._appConfig
                            });
                            this._handles.add(watchUtils_1.init(this._appConfig, ["drawBuffer", "mapPinLabel", "mapPin"], function (value, oldValue, propertyName) {
                                lookupGraphics.updateGraphics(propertyName, value);
                            }), "configuration");
                            return [4 /*yield*/, lookupLayerUtils_1.findConfiguredLookupLayers(this.view, config)];
                        case 1:
                            configuredLayers = _a.sent();
                            lookupLayers = lookupLayerUtils_1.getLookupLayers(configuredLayers);
                            lookupProps = {
                                config: config,
                                view: this.view,
                                lookupLayers: lookupLayers,
                                searchLayer: this._appConfig.enableSearchLayer && this._appConfig.searchLayer ? this._appConfig.searchLayer : null,
                                hideFeaturesOnLoad: this._appConfig.hideLookupLayers
                            };
                            return [4 /*yield*/, lookupLayerUtils_1.getSearchLayer(lookupProps)];
                        case 2:
                            searchLayer = _a.sent();
                            this.lookupResults = new DisplayLookupResults_1.default({
                                lookupLayers: lookupLayers,
                                lookupGraphics: lookupGraphics,
                                searchLayer: searchLayer,
                                config: this._appConfig,
                                view: this.view,
                                mapPanel: this.mapPanel,
                                container: 'resultsPanel'
                            });
                            this._handles.add(watchUtils_1.watch(this._appConfig, ["lookupLayers", "enableSearchLayer", "searchLayer"], function (value, oldValue, propertyName) { return tslib_1.__awaiter(_this, void 0, void 0, function () {
                                var configuredLayers_1, _a, searchLayer_1;
                                return tslib_1.__generator(this, function (_b) {
                                    switch (_b.label) {
                                        case 0:
                                            if (!(propertyName === "lookupLayers")) return [3 /*break*/, 3];
                                            // Lookup layers have been modified. Update results to 
                                            // only show included layers 
                                            config.lookupLayers = value;
                                            return [4 /*yield*/, lookupLayerUtils_1.findConfiguredLookupLayers(this.view, config)];
                                        case 1:
                                            configuredLayers_1 = _b.sent();
                                            _a = this.lookupResults;
                                            return [4 /*yield*/, lookupLayerUtils_1.getLookupLayers(configuredLayers_1)];
                                        case 2:
                                            _a.lookupLayers = _b.sent();
                                            if (this._results)
                                                this._displayResults(this._results);
                                            _b.label = 3;
                                        case 3:
                                            if (!(propertyName === "searchLayer" || propertyName === "enableSearchLayer")) return [3 /*break*/, 6];
                                            searchLayer_1 = null;
                                            if (!(propertyName === "enableSearchLayer")) return [3 /*break*/, 5];
                                            return [4 /*yield*/, lookupLayerUtils_1.getSearchLayer({
                                                    view: this.view,
                                                    lookupLayers: lookupLayers,
                                                    searchLayer: value,
                                                    hideFeaturesOnLoad: this._appConfig.hideLookupLayers
                                                })];
                                        case 4:
                                            searchLayer_1 = _b.sent();
                                            _b.label = 5;
                                        case 5:
                                            this.lookupResults.searchLayer = searchLayer_1;
                                            if (this._results)
                                                this._displayResults(this._results);
                                            _b.label = 6;
                                        case 6: return [2 /*return*/];
                                    }
                                });
                            }); }), "configuration");
                            this._addSearchWidget();
                            this._cleanUpHandles();
                            return [2 /*return*/];
                    }
                });
            });
        };
        LocationApp.prototype._addSearchWidget = function () {
            var _this = this;
            var _a;
            var _b = this._appConfig, searchConfiguration = _b.searchConfiguration, find = _b.find, findSource = _b.findSource;
            var sources = searchConfiguration === null || searchConfiguration === void 0 ? void 0 : searchConfiguration.sources;
            if (sources) {
                sources.forEach(function (source) {
                    var _a, _b;
                    if ((_a = source === null || source === void 0 ? void 0 : source.layer) === null || _a === void 0 ? void 0 : _a.url) {
                        source.layer = new FeatureLayer_1.default((_b = source === null || source === void 0 ? void 0 : source.layer) === null || _b === void 0 ? void 0 : _b.url);
                    }
                });
            }
            var searchProperties = tslib_1.__assign({
                view: this.view,
                resultGraphicEnabled: false,
                autoSelect: false,
                popupEnabled: false,
                container: "search",
                locationEnabled: true
            }, searchConfiguration);
            if (((_a = searchProperties === null || searchProperties === void 0 ? void 0 : searchProperties.sources) === null || _a === void 0 ? void 0 : _a.length) > 0) {
                searchProperties.includeDefaultSources = false;
            }
            this.searchWidget = new Search_1.default(searchProperties);
            this._handles.add(watchUtils_1.init(this._appConfig, ["searchConfiguration.sources", "searchConfiguration.activeSourceIndex", "searchConfiguration.searchAllEnabled", "searchConfiguration.customSearchSource", "searchConfiguration.featureSearchSource"], function (value, oldValue, propertyName) {
                if (propertyName === "searchConfiguration.activeSourceIndex") {
                    _this.searchWidget.activeSourceIndex = value;
                }
                if (propertyName === "searchConfiguration.searchAllEnabled") {
                    _this.searchWidget.searchAllEnabled = value;
                }
                if (propertyName === "searchConfiguration.customSearchSource") {
                    if (value) {
                        _this._addressSearchURL = value === null || value === void 0 ? void 0 : value.addressSearchURL;
                        // var url = value?.addressSearchURL;
                        var featureSearchURL = value === null || value === void 0 ? void 0 : value.featureSearchURL;
                        var customSearchSource = new SearchSource_1.default({
                            placeholder: "Enter Address or Intersection:",
                            getSuggestions: function (params) {
                                var _a, _b, _c, _d, _e, _f, _g;
                                if (((_a = params === null || params === void 0 ? void 0 : params.suggestTerm) === null || _a === void 0 ? void 0 : _a.replace(/ /, "/").split("/").length) > 1 && ((_b = params === null || params === void 0 ? void 0 : params.suggestTerm) === null || _b === void 0 ? void 0 : _b.replace(/ /, "/").split("/")[1].length) >= 2 && !isNaN((_c = params === null || params === void 0 ? void 0 : params.suggestTerm) === null || _c === void 0 ? void 0 : _c.replace(/ /, "/").split("/")[0])) {
                                    var addressSearchURL = "";
                                    if (_this._searchWidgetAddressType == "ADDRESS") {
                                        addressSearchURL = _this._addressSearchURL + "Addresses/" + params.suggestTerm.replace(/ /, "/") + "/true/true/true";
                                    }
                                    else if (_this._searchWidgetAddressType == "STREETS") {
                                        addressSearchURL = _this._addressSearchURL + "StreetAddresses/" + params.suggestTerm.replace(/ /, "/");
                                    }
                                    return request_1.default(addressSearchURL, {
                                        responseType: "json"
                                    }).then(function (results) {
                                        var mappedResultsArray = results.data.map(function (address) {
                                            if (_this._searchWidgetAddressType === "ADDRESS") {
                                                return {
                                                    key: "name",
                                                    text: address.Unit == "" ? address.FullName : address.FullName + " #" + address.Unit,
                                                    item: address,
                                                    sourceIndex: params.sourceIndex,
                                                    type: "address"
                                                };
                                            }
                                            else if (_this._searchWidgetAddressType === "STREETS") {
                                                return {
                                                    key: "name",
                                                    text: params.suggestTerm.replace(/ /, "/").split("/")[0] + " " + address.FullName,
                                                    item: { "addressItem": address, "addressNumber": params.suggestTerm.replace(/ /, "/").split("/")[0] },
                                                    sourceIndex: params.sourceIndex,
                                                    type: "street"
                                                };
                                            }
                                            else {
                                                return null;
                                            }
                                        });
                                        var filteredSuggestions = [];
                                        mappedResultsArray.forEach(function (element) {
                                            if (filteredSuggestions.find(function (s) { return s.text == element.text; }) == null) {
                                                filteredSuggestions.push(element);
                                            }
                                            else {
                                                if (filteredSuggestions.find(function (s) { return s.text == element.text; }).item.Level > element.item.Level) {
                                                    filteredSuggestions.splice(filteredSuggestions.indexOf(filteredSuggestions.find(function (f) { return f.text == element.item.Level; })), 1, element);
                                                }
                                            }
                                        });
                                        // Return Suggestion results to display
                                        // in the Search widget
                                        var filteredSortedSuggestions = filteredSuggestions.sort(function (n1, n2) {
                                            if (n1.text > n2.text) {
                                                return 1;
                                            }
                                            if (n1.text < n2.text) {
                                                return -1;
                                            }
                                            return 0;
                                        });
                                        return filteredSortedSuggestions.length == 0 ? null : filteredSortedSuggestions;
                                    });
                                }
                                else if (((_d = params === null || params === void 0 ? void 0 : params.suggestTerm) === null || _d === void 0 ? void 0 : _d.indexOf(" and ")) != -1 || ((_e = params === null || params === void 0 ? void 0 : params.suggestTerm) === null || _e === void 0 ? void 0 : _e.indexOf(" & ")) != -1) {
                                    if (((_f = params === null || params === void 0 ? void 0 : params.suggestTerm) === null || _f === void 0 ? void 0 : _f.split(/ and /).length) == 2 || ((_g = params === null || params === void 0 ? void 0 : params.suggestTerm) === null || _g === void 0 ? void 0 : _g.split(/ & /).length) == 2) {
                                        return request_1.default(_this._addressSearchURL + "Intersections/" + params.suggestTerm.replace(/ and | & /g, "/") + "/and", {
                                            responseType: "json"
                                        }).then(function (results) {
                                            var mappedResultsArray = results.data.map(function (address) {
                                                return {
                                                    key: "name",
                                                    text: address.FullName,
                                                    item: address,
                                                    sourceIndex: params.sourceIndex,
                                                    type: "intersection"
                                                };
                                            });
                                            var filteredSuggestions = [];
                                            mappedResultsArray.forEach(function (element) {
                                                if (filteredSuggestions.find(function (s) { return s.text == element.text; }) == null) {
                                                    filteredSuggestions.push(element);
                                                }
                                            });
                                            // Return Suggestion results to display
                                            // in the Search widget
                                            return filteredSuggestions;
                                        });
                                    }
                                    else {
                                        return null;
                                    }
                                }
                                else {
                                    return null;
                                }
                            },
                            // get results
                            getResults: function (params) {
                                if (params.suggestResult.item) {
                                    _this.lookupResults.state = 'loading';
                                    var getResultsURL = "";
                                    if (params.suggestResult.type == "address" || params.suggestResult.type == "street") {
                                        if (params.suggestResult.type == "address" && _this._searchWidgetAddressType == "ADDRESS") {
                                            getResultsURL = _this._addressSearchURL + "AddressLocation/" + params.suggestResult.item.HouseNum + "/" + params.suggestResult.item.Street.Tag + "/" + (params.suggestResult.item.Unit == "" ? " " : params.suggestResult.item.Unit) + "/" + _this._findAddressLevelByNumber(params.suggestResult.item.Level) + "/Parcel";
                                        }
                                        else if (_this._searchWidgetAddressType == "STREETS" || params.suggestResult.type == "street") {
                                            _this._searchWidgetAddressType = "STREETS";
                                            getResultsURL = _this._addressSearchURL + "StreetLocation/" + params.suggestResult.item.addressNumber + "/" + params.suggestResult.item.addressItem.Tag + "/0";
                                        }
                                        return request_1.default(getResultsURL, {
                                            responseType: "json"
                                        }).then(function (results) {
                                            var _a, _b;
                                            if (_this._searchWidgetAddressType == "ADDRESS") {
                                                var promises_1 = [];
                                                (_a = results.data) === null || _a === void 0 ? void 0 : _a.forEach(function (resultLocation) {
                                                    var spatialReference = new geometry_1.SpatialReference({ wkid: 2244 });
                                                    var parcelFeatureLayer = new FeatureLayer_1.default({ "url": featureSearchURL, "spatialReference": spatialReference });
                                                    var parcelQuery = parcelFeatureLayer.createQuery();
                                                    parcelQuery.where = "1=1";
                                                    parcelQuery.geometry = new Point_1.default({ x: resultLocation.X, y: resultLocation.Y, spatialReference: spatialReference });
                                                    promises_1.push(parcelFeatureLayer.queryFeatures(parcelQuery).then(function (queryResults) {
                                                        var _a;
                                                        if (((_a = queryResults.features) === null || _a === void 0 ? void 0 : _a.length) > 0) {
                                                            var searchResult = {
                                                                extent: queryResults.features[0].geometry.extent,
                                                                feature: queryResults.features[0],
                                                                name: "Feature" //feature.properties.label
                                                            };
                                                            return searchResult; //[searchResult];
                                                        }
                                                        else {
                                                            if (resultLocation.Level == 0) {
                                                                parcelQuery.geometry = null;
                                                                parcelQuery.where = "PARCEL_TAG = " + resultLocation.Tag;
                                                                return parcelFeatureLayer.queryFeatures(parcelQuery).then(function (queryResults) {
                                                                    var _a;
                                                                    if (((_a = queryResults.features) === null || _a === void 0 ? void 0 : _a.length) > 0) {
                                                                        var searchResult = {
                                                                            extent: queryResults.features[0].geometry.extent,
                                                                            feature: queryResults.features[0],
                                                                            name: "Feature" //feature.properties.label
                                                                        };
                                                                        return searchResult; //[searchResult];
                                                                    }
                                                                    else {
                                                                        if (_this.lookupResults._empty == true) {
                                                                            _this.lookupResults.state = 'init';
                                                                        }
                                                                        else {
                                                                            _this.lookupResults.state = 'ready';
                                                                        }
                                                                        return null;
                                                                    }
                                                                });
                                                            }
                                                            else {
                                                                if (_this.lookupResults._empty == true) {
                                                                    _this.lookupResults.state = 'init';
                                                                }
                                                                else {
                                                                    _this.lookupResults.state = 'ready';
                                                                }
                                                                return null;
                                                            }
                                                        }
                                                    }));
                                                });
                                                return Promise.all(promises_1);
                                            }
                                            else if (_this._searchWidgetAddressType == "STREETS") {
                                                _this._searchWidgetAddressType = "ADDRESS";
                                                if (((_b = results === null || results === void 0 ? void 0 : results.data) === null || _b === void 0 ? void 0 : _b.length) > 0) {
                                                    var spatialReference = new geometry_1.SpatialReference({ wkid: 2244 });
                                                    var intersectionPoint = new Point_1.default({
                                                        x: results.data[0].X,
                                                        y: results.data[0].Y,
                                                        spatialReference: spatialReference
                                                    });
                                                    return geometryUtils_1.projectPoint(intersectionPoint, _this.view.spatialReference).then(function (projectedIntersectionPoint) {
                                                        var markerSymbol = new symbols_1.SimpleMarkerSymbol({
                                                            outline: {
                                                                width: 1
                                                            },
                                                            size: 14,
                                                            color: [0, 0, 255, 1]
                                                        });
                                                        var graphic = new Graphic_1.default({
                                                            geometry: projectedIntersectionPoint,
                                                            symbol: markerSymbol
                                                        });
                                                        var searchResult = {
                                                            extent: _this.view.extent,
                                                            feature: graphic,
                                                            name: "Feature" //feature.properties.label
                                                        };
                                                        return [searchResult];
                                                    });
                                                }
                                                else {
                                                    if (_this.lookupResults._empty == true) {
                                                        _this.lookupResults.state = 'init';
                                                    }
                                                    else {
                                                        _this.lookupResults.state = 'ready';
                                                    }
                                                    return null;
                                                }
                                            }
                                        }).catch(function (error) {
                                            console.error("Could not find address location.", error);
                                            if (_this.lookupResults._empty == true) {
                                                _this.lookupResults.state = 'init';
                                            }
                                            else {
                                                _this.lookupResults.state = 'ready';
                                            }
                                            return null;
                                        });
                                    }
                                    else if (params.suggestResult.type == "intersection") {
                                        getResultsURL = _this._addressSearchURL + "IntersectionLocation/" + params.suggestResult.item.ID;
                                        return request_1.default(getResultsURL, {
                                            responseType: "json"
                                        }).then(function (results) {
                                            var _a;
                                            if (((_a = results === null || results === void 0 ? void 0 : results.data) === null || _a === void 0 ? void 0 : _a.length) > 0) {
                                                var spatialReference = new geometry_1.SpatialReference({ wkid: 2244 });
                                                var intersectionPoint = new Point_1.default({
                                                    x: results.data[0].X,
                                                    y: results.data[0].Y,
                                                    spatialReference: spatialReference
                                                });
                                                return geometryUtils_1.projectPoint(intersectionPoint, _this.view.spatialReference).then(function (projectedIntersectionPoint) {
                                                    var markerSymbol = new symbols_1.SimpleMarkerSymbol({
                                                        outline: {
                                                            width: 1
                                                        },
                                                        size: 14,
                                                        color: [0, 0, 255, 1]
                                                    });
                                                    var graphic = new Graphic_1.default({
                                                        geometry: projectedIntersectionPoint,
                                                        symbol: markerSymbol
                                                    });
                                                    var searchResult = {
                                                        extent: _this.view.extent,
                                                        feature: graphic,
                                                        name: "Feature" //feature.properties.label
                                                    };
                                                    return [searchResult];
                                                });
                                            }
                                            else {
                                                if (_this.lookupResults._empty == true) {
                                                    _this.lookupResults.state = 'init';
                                                }
                                                else {
                                                    _this.lookupResults.state = 'ready';
                                                }
                                                return null;
                                            }
                                        }).catch(function (error) {
                                            console.error("Could not find intersection location.", error);
                                            if (_this.lookupResults._empty == true) {
                                                _this.lookupResults.state = 'init';
                                            }
                                            else {
                                                _this.lookupResults.state = 'ready';
                                            }
                                            return null;
                                        });
                                    }
                                }
                                else if (params.suggestResult.location) {
                                    _this.processMapViewLocation(params.suggestResult.location);
                                    _this.view.goTo(params.suggestResult.location);
                                }
                                else {
                                    _this.searchWidget.suggest(params.suggestResult.text);
                                }
                            }
                        });
                        customSearchSource["name"] = "Search by Address or intersection";
                        var parcelCustomSearchSource = new SearchSource_1.default({
                            placeholder: "Enter Parcel Number:",
                            getSuggestions: function (params) {
                                var _a;
                                if ((params === null || params === void 0 ? void 0 : params.suggestTerm) && !isNaN(params === null || params === void 0 ? void 0 : params.suggestTerm) && ((_a = params === null || params === void 0 ? void 0 : params.suggestTerm) === null || _a === void 0 ? void 0 : _a.length) > 3) {
                                    var spatialReference = new geometry_1.SpatialReference({ wkid: 2244 });
                                    var parcelFeatureLayer = new FeatureLayer_1.default({ "url": featureSearchURL, "spatialReference": spatialReference });
                                    var parcelQuery = parcelFeatureLayer.createQuery();
                                    parcelQuery.where = "PARCEL_C like '%" + (params === null || params === void 0 ? void 0 : params.suggestTerm) + "%'";
                                    parcelQuery.outFields = ['*'];
                                    parcelQuery.returnGeometry = true;
                                    return parcelFeatureLayer.queryFeatures(parcelQuery).then(function (queryResults) {
                                        var mappedResultsArray = queryResults.features.map(function (feature) {
                                            return {
                                                key: "name",
                                                text: feature.attributes["PARCEL_C"],
                                                item: feature,
                                                sourceIndex: params.sourceIndex,
                                                type: "localParcelNumber"
                                            };
                                        });
                                        return mappedResultsArray;
                                    });
                                }
                            },
                            getResults: function (params) {
                                if (params.suggestResult.type == "localParcelNumber") {
                                    var searchResult = {
                                        extent: params.suggestResult.item.geometry.extent,
                                        feature: params.suggestResult.item,
                                        name: "Feature" //feature.properties.label
                                    };
                                    return Promise.resolve().then(function () {
                                        return [searchResult];
                                    });
                                    // this._processParcelClickResults(params.suggestResult.item);
                                    // return null;
                                }
                                else if (params.suggestResult.location) {
                                    _this.processMapViewLocation(params.suggestResult.location);
                                    _this.view.goTo(params.suggestResult.location);
                                }
                            }
                        });
                        parcelCustomSearchSource["name"] = "Search by Local Parcel Number";
                        var stateParcelCustomSearchSource = new SearchSource_1.default({
                            placeholder: "Enter State Parcel Number:",
                            getSuggestions: function (params) {
                                var _a;
                                if ((params === null || params === void 0 ? void 0 : params.suggestTerm) && ((_a = params === null || params === void 0 ? void 0 : params.suggestTerm) === null || _a === void 0 ? void 0 : _a.length) > 15) {
                                    var spatialReference = new geometry_1.SpatialReference({ wkid: 2244 });
                                    var parcelFeatureLayer = new FeatureLayer_1.default({ "url": featureSearchURL, "spatialReference": spatialReference });
                                    var parcelQuery = parcelFeatureLayer.createQuery();
                                    parcelQuery.where = "STATEPARCELNUMBER like '%" + (params === null || params === void 0 ? void 0 : params.suggestTerm) + "%'";
                                    parcelQuery.outFields = ['*'];
                                    parcelQuery.returnGeometry = true;
                                    return parcelFeatureLayer.queryFeatures(parcelQuery).then(function (queryResults) {
                                        var mappedResultsArray = queryResults.features.map(function (feature) {
                                            return {
                                                key: "name",
                                                text: feature.attributes["STATEPARCELNUMBER"],
                                                item: feature,
                                                sourceIndex: params.sourceIndex,
                                                type: "stateParcelNumber"
                                            };
                                        });
                                        return mappedResultsArray;
                                    });
                                }
                            },
                            getResults: function (params) {
                                if (params.suggestResult.type == "stateParcelNumber") {
                                    var searchResult = {
                                        extent: params.suggestResult.item.geometry.extent,
                                        feature: params.suggestResult.item,
                                        name: "Feature" //feature.properties.label
                                    };
                                    return Promise.resolve().then(function () {
                                        return [searchResult];
                                    });
                                    // this._processParcelClickResults(params.suggestResult.item);
                                    // return null;
                                }
                                else if (params.suggestResult.location) {
                                    _this.processMapViewLocation(params.suggestResult.location);
                                    _this.view.goTo(params.suggestResult.location);
                                }
                            }
                        });
                        stateParcelCustomSearchSource["name"] = "Search by State Parcel Number";
                        var ownerNameCustomSearchSource = new SearchSource_1.default({
                            placeholder: "Enter Owner Name:",
                            getSuggestions: function (params) {
                                var _a;
                                if ((params === null || params === void 0 ? void 0 : params.suggestTerm) && ((_a = params === null || params === void 0 ? void 0 : params.suggestTerm) === null || _a === void 0 ? void 0 : _a.length) > 3) {
                                    var spatialReference = new geometry_1.SpatialReference({ wkid: 2244 });
                                    var parcelFeatureLayer = new FeatureLayer_1.default({ "url": featureSearchURL, "spatialReference": spatialReference });
                                    var parcelQuery = parcelFeatureLayer.createQuery();
                                    parcelQuery.where = "FULLOWNERNAME like '%" + (params === null || params === void 0 ? void 0 : params.suggestTerm.toUpperCase()) + "%'";
                                    parcelQuery.outFields = ['*'];
                                    parcelQuery.returnGeometry = true;
                                    return parcelFeatureLayer.queryFeatures(parcelQuery).then(function (queryResults) {
                                        var mappedResultsArray = queryResults.features.map(function (feature) {
                                            return {
                                                key: "name",
                                                text: feature.attributes["FULLOWNERNAME"],
                                                item: feature,
                                                sourceIndex: params.sourceIndex,
                                                type: "ownerName"
                                            };
                                        });
                                        return mappedResultsArray;
                                    });
                                }
                            },
                            getResults: function (params) {
                                if (params.suggestResult.type == "ownerName") {
                                    var searchResult = {
                                        extent: params.suggestResult.item.geometry.extent,
                                        feature: params.suggestResult.item,
                                        name: "Feature" //feature.properties.label
                                    };
                                    return Promise.resolve().then(function () {
                                        return [searchResult];
                                    });
                                }
                                else if (params.suggestResult.location) {
                                    _this.processMapViewLocation(params.suggestResult.location);
                                    _this.view.goTo(params.suggestResult.location);
                                }
                            }
                        });
                        ownerNameCustomSearchSource["name"] = "Search by Owner Name";
                        var sourcesCollection = new Collection_1.default([customSearchSource, parcelCustomSearchSource, stateParcelCustomSearchSource, ownerNameCustomSearchSource]);
                        _this.searchWidget.sources = sourcesCollection;
                    }
                }
            }), "configuration");
            // If there's a find url param search for it
            if (find) {
                watchUtils_1.whenFalseOnce(this.view, "updating", function () {
                    if (findSource) {
                        _this.searchWidget.activeSourceIndex = findSource;
                    }
                    _this._urlParamsSearch = true;
                    _this.lookupResults.state = 'loading';
                    _this.searchWidget.suggest(decodeURIComponent(find));
                });
            }
            var handle = this.searchWidget.viewModel.watch('state', function (state) {
                if (state === 'ready') {
                    handle.remove();
                    // conditionally hide on tablet
                    if (!_this.view.container.classList.contains('tablet-show')) {
                        _this.view.container.classList.add('tablet-hide');
                    }
                    // force search within map if nothing is configured
                    if (!searchConfiguration) {
                        _this.searchWidget.viewModel.allSources.forEach(function (source) {
                            source.withinViewEnabled = true;
                        });
                    }
                }
            });
            this.searchWidget.on('search-clear', function () {
                _this._cleanUpResults();
                // Remove find url param
                _this._updateUrlParam();
                // this.mapPanel.resetExtent();
                if (_this.view.popup) {
                    _this.view.popup.close();
                }
                _this._searchWidgetAddressType = "ADDRESS";
            });
            this.searchWidget.on('search-complete', function (results) { return tslib_1.__awaiter(_this, void 0, void 0, function () {
                return tslib_1.__generator(this, function (_a) {
                    this._displayResults(results);
                    return [2 /*return*/];
                });
            }); });
            this.searchWidget.on('suggest-complete', function (suggestions) { return tslib_1.__awaiter(_this, void 0, void 0, function () {
                var streetName_1, listItems, j, listItems, j, listItems, j;
                var _this = this;
                var _a, _b, _c;
                return tslib_1.__generator(this, function (_d) {
                    if (this.searchWidget.activeSourceIndex == 0) {
                        if ((suggestions === null || suggestions === void 0 ? void 0 : suggestions.numResults) > 0 && this._urlParamsSearch == true) {
                            this._urlParamsSearch = false;
                            this.searchWidget.search(suggestions.results[0].results[0]);
                            this.lookupResults.errorHTMLText = null;
                        }
                        else if ((suggestions === null || suggestions === void 0 ? void 0 : suggestions.numResults) == 0 && this._urlParamsSearch == true && this._searchWidgetAddressType === "ADDRESS") {
                            this._searchWidgetAddressType = "STREETS";
                            this.searchWidget.suggest(suggestions.searchTerm);
                            this.lookupResults.errorHTMLText = null;
                        }
                        else if ((suggestions === null || suggestions === void 0 ? void 0 : suggestions.numResults) === 0 && this._searchWidgetAddressType === "STREETS") {
                            this._searchWidgetAddressType = "ADDRESS";
                            if (!isNaN(Number(this.searchWidget.searchTerm.split(/ /)[0])) && ((_a = this.searchWidget.searchTerm) === null || _a === void 0 ? void 0 : _a.replace(/ /, "/").split("/").length) > 1 && ((_b = this.searchWidget.searchTerm) === null || _b === void 0 ? void 0 : _b.replace(/ /, "/").split("/")[1].length) >= 2) {
                                streetName_1 = (_c = this.searchWidget.searchTerm) === null || _c === void 0 ? void 0 : _c.replace(/ /, "/").split("/")[1];
                                request_1.default(this._addressSearchURL + "Streets/" + streetName_1, {
                                    responseType: "json"
                                }).then(function (response) {
                                    var _a;
                                    console.log(response);
                                    var message;
                                    if (((_a = response === null || response === void 0 ? void 0 : response.data) === null || _a === void 0 ? void 0 : _a.length) > 0) {
                                        var data_1 = response.data;
                                        var distinctStreetNames = tslib_1.__spread(new Set(data_1.map(function (x) { return x.FullName; })));
                                        message = "Address you entered is not valid.<br/><hr/>";
                                        if (distinctStreetNames.length == 1) {
                                            var ranges = data_1.map(function (x) { return ({ "min": Math.min(x.FromLeft, x.FromRight), "max": Math.max(x.ToLeft, x.ToRight) }); });
                                            var sortedRanges = ranges.sort(function (a, b) { return (a.min - b.min); });
                                            var finalRanges = [{ "min": sortedRanges[0].min, "max": sortedRanges[0].max }];
                                            for (var sr = 1; sr < sortedRanges.length; sr++) {
                                                if (sortedRanges[sr].min - sortedRanges[sr - 1].max > 99) {
                                                    finalRanges.push(sortedRanges[sr]);
                                                }
                                                else {
                                                    finalRanges[(finalRanges.length - 1)]["max"] = sortedRanges[sr].max;
                                                }
                                            }
                                            message = message + "Valid address ranges for " + streetName_1.toUpperCase() + " are:<br/><ul>";
                                            finalRanges.forEach(function (range) {
                                                message = message + "<li>" + range.min + " to " + range.max + "</li>";
                                            });
                                        }
                                        else {
                                            message = message + "Valid address ranges are:<br/><ul>";
                                            distinctStreetNames.forEach(function (streetName) {
                                                var maxAddress = Math.max.apply(Math, tslib_1.__spread(data_1.filter(function (x) { return x.FullName == streetName; }).map(function (x) { return x.ToLeft; }), data_1.filter(function (x) { return x.FullName == streetName; }).map(function (x) { return x.ToRight; })));
                                                var minAddress = Math.min.apply(Math, tslib_1.__spread(data_1.filter(function (x) { return x.FullName == streetName; }).map(function (x) { return x.FromLeft; }), data_1.filter(function (x) { return x.FullName == streetName; }).map(function (x) { return x.FromRight; })));
                                                message = message + "<li>" + streetName.toUpperCase() + ": " + minAddress + " to " + maxAddress + "</li>";
                                            });
                                        }
                                        message = message + "</ul>";
                                    }
                                    else {
                                        message = "The address you entered could not be found within Marion County.";
                                    }
                                    _this.lookupResults.state = "ready";
                                    _this.lookupResults.errorHTMLText = message;
                                    _this._detailPanel.collapse();
                                });
                            }
                        }
                        else {
                            this.lookupResults.errorHTMLText = null;
                        }
                    }
                    else if (this.searchWidget.activeSourceIndex == 1) {
                        if ((suggestions === null || suggestions === void 0 ? void 0 : suggestions.numResults) > 0 && this._urlParamsSearch == true) {
                            listItems = document.getElementsByClassName("esri-search__source esri-menu__list-item");
                            for (j = 0; j < listItems.length; j++) {
                                if (listItems[j]["data-source-index"] == 1) {
                                    if (!listItems[j].classList.contains("esri-menu__list-item--active")) {
                                        listItems[j].classList.add("esri-menu__list-item--active");
                                        break;
                                    }
                                }
                            }
                            ;
                            this._urlParamsSearch = false;
                            this.searchWidget.search(suggestions.results[0].results[0]);
                            this.lookupResults.errorHTMLText = null;
                        }
                    }
                    else if (this.searchWidget.activeSourceIndex == 2) {
                        if ((suggestions === null || suggestions === void 0 ? void 0 : suggestions.numResults) > 0 && this._urlParamsSearch == true) {
                            listItems = document.getElementsByClassName("esri-search__source esri-menu__list-item");
                            for (j = 0; j < listItems.length; j++) {
                                if (listItems[j]["data-source-index"] == 2) {
                                    if (!listItems[j].classList.contains("esri-menu__list-item--active")) {
                                        listItems[j].classList.add("esri-menu__list-item--active");
                                        break;
                                    }
                                }
                            }
                            ;
                            this._urlParamsSearch = false;
                            this.searchWidget.search(suggestions.results[0].results[0]);
                            this.lookupResults.errorHTMLText = null;
                        }
                    }
                    else if (this.searchWidget.activeSourceIndex == 3) {
                        if ((suggestions === null || suggestions === void 0 ? void 0 : suggestions.numResults) > 0 && this._urlParamsSearch == true) {
                            listItems = document.getElementsByClassName("esri-search__source esri-menu__list-item");
                            for (j = 0; j < listItems.length; j++) {
                                if (listItems[j]["data-source-index"] == 3) {
                                    if (!listItems[j].classList.contains("esri-menu__list-item--active")) {
                                        listItems[j].classList.add("esri-menu__list-item--active");
                                        break;
                                    }
                                }
                            }
                            ;
                            this._urlParamsSearch = false;
                            this.searchWidget.search(suggestions.results[0].results[0]);
                            this.lookupResults.errorHTMLText = null;
                        }
                    }
                    else {
                        this.lookupResults.errorHTMLText = null;
                    }
                    return [2 /*return*/];
                });
            }); });
            this.searchWidget.on('search-start', function () { return tslib_1.__awaiter(_this, void 0, void 0, function () {
                var _a, _b, _c, _d;
                return tslib_1.__generator(this, function (_e) {
                    if (((_a = this.searchWidget.suggestions) === null || _a === void 0 ? void 0 : _a.length) > 0 && this.searchWidget.suggestions[0]["results"].length == 0 && ((_b = this.searchWidget.searchTerm) === null || _b === void 0 ? void 0 : _b.replace(/ /, "/").split("/").length) > 1 && ((_c = this.searchWidget.searchTerm) === null || _c === void 0 ? void 0 : _c.replace(/ /, "/").split("/")[1].length) >= 2 && !isNaN(Number((_d = this.searchWidget.searchTerm) === null || _d === void 0 ? void 0 : _d.replace(/ /, "/").split("/")[0]))) {
                        this._searchWidgetAddressType = "STREETS";
                        this.searchWidget.suggest(this.searchWidget.searchTerm);
                    }
                    return [2 /*return*/];
                });
            }); });
            this.searchWidget.watch("activeSource", function (source) {
                var listItems = document.getElementsByClassName("esri-search__source esri-menu__list-item");
                for (var j = 0; j < listItems.length; j++) {
                    if (listItems[j].classList.contains("esri-menu__list-item--active")) {
                        listItems[j].classList.remove("esri-menu__list-item--active");
                    }
                }
                ;
                _this.searchWidget.searchTerm = "";
            });
            // We also want to search for locations when users click on the map
            this.view.on('click', function (e) { return tslib_1.__awaiter(_this, void 0, void 0, function () {
                var mapPoint;
                var _this = this;
                return tslib_1.__generator(this, function (_a) {
                    mapPoint = e.mapPoint;
                    this.view.hitTest(e).then(function (response) {
                        var _a;
                        if (((_a = response.results) === null || _a === void 0 ? void 0 : _a.length) == 0) {
                            _this.processMapViewLocation(mapPoint);
                        }
                        else {
                            var pointResults = response.results.filter(function (r) { return r.graphic.geometry.type == "point"; });
                            if (pointResults.length > 0) {
                                _this.view.popup.open({
                                    features: pointResults.map(function (pr) { return pr.graphic; }),
                                    location: pointResults.length == 1 ? pointResults[0].graphic.geometry : mapPoint
                                });
                            }
                            else {
                                _this.processMapViewLocation(mapPoint);
                            }
                        }
                    });
                    return [2 /*return*/];
                });
            }); });
            this.view.popup = new Popup_1.default();
            this.view.popup.autoOpenEnabled = false;
        };
        LocationApp.prototype.processMapViewLocation = function (mapPoint) {
            var _a, _b, _c, _d, _e;
            return tslib_1.__awaiter(this, void 0, void 0, function () {
                var zoningLayersLayer, sketchGraphicsLayer, popupFeatures_1, popupLocation, spatialReference;
                var _this = this;
                return tslib_1.__generator(this, function (_f) {
                    this._cleanUpResults();
                    zoningLayersLayer = this.view.map.allLayers.find(function (x) { return x.title == "Zoning Layers"; });
                    if (zoningLayersLayer && (zoningLayersLayer === null || zoningLayersLayer === void 0 ? void 0 : zoningLayersLayer.visible) == true && ((_a = window["sketchWidget"]) === null || _a === void 0 ? void 0 : _a.activeTool)) {
                        // this.view.popup = null;
                        this.view.popup.close();
                        return [2 /*return*/];
                    }
                    else {
                        sketchGraphicsLayer = this.view.map.findLayerById("SketchToolGraphicsLayer");
                        if (sketchGraphicsLayer.graphics.length > 0 && ((_b = this.view.popup) === null || _b === void 0 ? void 0 : _b.visible) == false) {
                            popupFeatures_1 = [];
                            this.view.popup.features.forEach(function (graphic) {
                                var popupFeature = graphic.clone();
                                popupFeatures_1.push(popupFeature);
                            });
                            popupLocation = this.view.popup.location.clone();
                            // this.view.popup = new Popup();
                            // this.view.popup.open({
                            // 	features: popupFeatures,
                            // 	location: popupLocation
                            // });
                            return [2 /*return*/];
                        }
                        // this.view.popup = null;
                        this.view.popup.close();
                        if ((_d = (_c = this.base) === null || _c === void 0 ? void 0 : _c.config) === null || _d === void 0 ? void 0 : _d.searchLayer) {
                            this._cleanUpResults();
                        }
                        (_e = sketchGraphicsLayer === null || sketchGraphicsLayer === void 0 ? void 0 : sketchGraphicsLayer.graphics) === null || _e === void 0 ? void 0 : _e.removeAll();
                        this.lookupResults.state = 'loading';
                        spatialReference = new geometry_1.SpatialReference({ wkid: 2244 });
                        geometryUtils_1.projectPoint(mapPoint, spatialReference).then(function (projectedPoint) {
                            _this.searchWidget.activeSourceIndex = 0;
                            _this._parcelFeatureQuery(projectedPoint, spatialReference, mapPoint);
                        }).catch(function (error) {
                            console.error("Map point failed to project.");
                            if (_this.lookupResults._empty == true) {
                                _this.lookupResults.state = 'init';
                            }
                            else {
                                _this.lookupResults.state = 'ready';
                            }
                        });
                    }
                    return [2 /*return*/];
                });
            });
        };
        LocationApp.prototype._findAddressLevelByNumber = function (levelNumber) {
            switch (levelNumber) {
                case 0:
                    return "Parcel";
                case 1:
                    return "Building";
                case 2:
                    return "Unit";
                default:
                    return "";
            }
        };
        LocationApp.prototype._parcelFeatureQuery = function (projectedPoint, spatialReference, originalPoint) {
            var _this = this;
            var searchConfiguration = this._appConfig.searchConfiguration;
            var parcelFeatureLayer = new FeatureLayer_1.default({ "url": searchConfiguration.customSearchSource.featureSearchURL, "spatialReference": spatialReference });
            var parcelQuery = parcelFeatureLayer.createQuery();
            parcelQuery.where = "1=1";
            parcelQuery.geometry = projectedPoint;
            parcelFeatureLayer.queryFeatures(parcelQuery).then(function (queryResults) {
                _this._processParcelClickResults(queryResults, projectedPoint, originalPoint);
            }).catch(function (error) {
                console.error("Failed to find parcel in selected location.");
                _this.lookupResults.state = 'loading';
            });
        };
        LocationApp.prototype._processParcelClickResults = function (queryResults, projectedPoint, originalPoint) {
            var _this = this;
            var _a;
            if (((_a = queryResults.features) === null || _a === void 0 ? void 0 : _a.length) > 0) {
                var searchResult = {
                    extent: queryResults.features[0].geometry.extent,
                    feature: queryResults.features[0],
                    name: "Feature" //feature.properties.label
                };
                var searchResponse = {
                    activeSourceIndex: 0,
                    errors: [],
                    numResults: 1,
                    searchTerm: "Address",
                    results: [{
                            results: [searchResult],
                            source: {},
                            sourceIndex: 0
                        }]
                };
                this._displayResults(searchResponse);
                var feature = queryResults.features[0];
                var streetAddress = (feature.attributes["STNUMBER"] + " " + (feature.attributes["PRE_DIR"] == null ? "" : feature.attributes["PRE_DIR"] + " ") + feature.attributes["FULL_STNAME"] + " " + (feature.attributes["SUF_DIR"] == null ? "" : feature.attributes["SUF_DIR"] + " ")).trim();
                this.searchWidget.searchTerm = streetAddress;
                this._updateUrlParam(encodeURIComponent(streetAddress));
            }
            else {
                request_1.default((this._appConfig.geocode.reverseGeocodeURL_0 + projectedPoint.x + "," + projectedPoint.y + this._appConfig.geocode.reverseGeocodeURL_1), {
                    responseType: "json"
                }).then(function (geocodeResponse) {
                    var _a, _b, _c, _d, _e, _f, _g, _h, _j, _k;
                    _this.searchWidget.searchTerm = (_b = (_a = geocodeResponse.data) === null || _a === void 0 ? void 0 : _a.address) === null || _b === void 0 ? void 0 : _b.Street;
                    if (_this.view.popup && _this.view.popup.visible == true) {
                        _this.view.popup.title = (_d = (_c = geocodeResponse.data) === null || _c === void 0 ? void 0 : _c.address) === null || _d === void 0 ? void 0 : _d.Street;
                    }
                    else {
                        // this.view.popup.open({
                        // 	// Set the popup's title to the coordinates of the clicked location
                        // 	title: "Selected location",
                        // 	content: geocodeResponse.data?.address?.Street,
                        // 	location: originalPoint // Set the location of the popup to the clicked location
                        // });
                    }
                    _this._updateUrlParam(encodeURIComponent((_f = (_e = geocodeResponse.data) === null || _e === void 0 ? void 0 : _e.address) === null || _f === void 0 ? void 0 : _f.Street));
                    _this._displayNoResultsMessage(originalPoint, { "PopupContent": (_h = (_g = geocodeResponse.data) === null || _g === void 0 ? void 0 : _g.address) === null || _h === void 0 ? void 0 : _h.Street, "PopupTitle": "Selected Location", "FullAddress": (_k = (_j = geocodeResponse.data) === null || _j === void 0 ? void 0 : _j.address) === null || _k === void 0 ? void 0 : _k.Street });
                }).catch(function (error) {
                    console.error(error);
                    _this.searchWidget.searchTerm = "No address found in selected location.";
                    if (_this.view.popup && _this.view.popup.visible == true) {
                        _this.view.popup.title = "No address found in selected location.";
                    }
                    else {
                        // this.view.popup = new Popup();
                        _this.view.popup.open({
                            // Set the popup's title to the coordinates of the clicked location
                            title: "",
                            content: "No address found in selected location.",
                            location: originalPoint // Set the location of the popup to the clicked location
                        });
                    }
                    _this._updateUrlParam();
                    _this._displayNoResultsMessage(originalPoint, { "PopupContent": "No address found in the selected location.", "PopupTitle": "Selected Location" });
                });
            }
        };
        LocationApp.prototype._displayNoResultsMessage = function (geometry, attributes) {
            // display no results message
            var g = new Graphic_1.default({ geometry: geometry, attributes: attributes });
            this.lookupResults.lookupGraphics.graphics = [g];
            this._generateSearchResults(g);
            this.searchWidget.activeMenu = null;
        };
        LocationApp.prototype._generateSearchResults = function (location) {
            return tslib_1.__awaiter(this, void 0, void 0, function () {
                return tslib_1.__generator(this, function (_a) {
                    // collapse the detail panel when results are found
                    if (this._detailPanel) {
                        this._detailPanel.collapse();
                    }
                    this.lookupResults && this.lookupResults.queryFeatures(location, 0);
                    return [2 /*return*/];
                });
            });
        };
        LocationApp.prototype._displayResults = function (results) {
            var _a, _b;
            return tslib_1.__awaiter(this, void 0, void 0, function () {
                var index, searchLayer, features, popupFeatures, projectFeaturePromises;
                var _this = this;
                return tslib_1.__generator(this, function (_c) {
                    switch (_c.label) {
                        case 0:
                            this._results = results;
                            if (!((_a = this.base.config) === null || _a === void 0 ? void 0 : _a.searchLayer)) {
                                this._cleanUpResults();
                            }
                            if (!(results.numResults > 0)) return [3 /*break*/, 2];
                            this._results = results;
                            index = results && results.activeSourceIndex ? results.activeSourceIndex : null;
                            this._updateUrlParam(encodeURIComponent(this.searchWidget.searchTerm), index);
                            searchLayer = ((_b = this.lookupResults) === null || _b === void 0 ? void 0 : _b.searchLayer) || null;
                            return [4 /*yield*/, lookupLayerUtils_1.getSearchGeometries({
                                    searchLayer: searchLayer,
                                    config: this._appConfig,
                                    view: this.view,
                                    results: results
                                })];
                        case 1:
                            features = _c.sent();
                            popupFeatures = [];
                            projectFeaturePromises = [];
                            features.forEach(function (feature) {
                                if (feature.geometry.type == "polygon") {
                                    projectFeaturePromises.push(geometryUtils_2.projectPolygon(feature.geometry, _this.view.spatialReference).then(function (projectedPolygon) {
                                        var popupFeature = feature.clone();
                                        popupFeature.geometry = projectedPolygon.centroid;
                                        popupFeature.extraOption = feature.clone();
                                        popupFeature.extraOption.attributes ? popupFeature.extraOption.attributes["FullAddress"] = _this._calculateParcelAddress(popupFeature) : popupFeature.extraOption.attributes = { "FullAddress": _this._calculateParcelAddress(popupFeature) };
                                        // popupFeature.popupTemplate = new PopupTemplate({
                                        // 	title: "Property information for: " + popupFeature.attributes["PARCEL_C"],
                                        // 	content: this._parcelPopupContent(popupFeature)
                                        // });
                                        popupFeature.attributes["PopupTitle"] = "Selected Address",
                                            popupFeature.attributes["PopupContent"] = _this._calculateParcelAddress(popupFeature);
                                        popupFeatures.push(popupFeature);
                                        return popupFeature;
                                    }));
                                }
                                else if (feature.geometry.type == "point") {
                                    projectFeaturePromises.push(geometryUtils_1.projectPoint(feature.geometry, _this.view.spatialReference).then(function (projectedPoint) {
                                        var popupFeature = feature.clone();
                                        popupFeature.geometry = projectedPoint;
                                        popupFeature.extraOption = feature.clone();
                                        popupFeature.extraOption.attributes ? popupFeature.extraOption.attributes["FullAddress"] = _this.searchWidget.searchTerm : popupFeature.extraOption.attributes = { "FullAddress": _this.searchWidget.searchTerm };
                                        // popupFeature.popupTemplate = new PopupTemplate({
                                        // 	title: "Selected location",
                                        // 	content: this.searchWidget.searchTerm
                                        // });
                                        popupFeature.attributes ? popupFeature.attributes["PopupTitle"] = "Selected Address" : popupFeature.attributes = { "PopupTitle": "Selected Address" },
                                            popupFeature.attributes["PopupContent"] = _this.searchWidget.searchTerm;
                                        popupFeatures.push(popupFeature);
                                        return popupFeature;
                                    }));
                                }
                            });
                            Promise.all(projectFeaturePromises).then(function (resultsToShow) {
                                // this.view.popup.open({
                                // 	features: resultsToShow
                                // });
                                _this.lookupResults.lookupGraphics.graphics = resultsToShow;
                                _this._generateSearchResults(resultsToShow[0].extraOption);
                            });
                            _c.label = 2;
                        case 2: return [2 /*return*/];
                    }
                });
            });
        };
        LocationApp.prototype._calculateParcelAddress = function (feature) {
            return feature.attributes["STNUMBER"] + " " + (feature.attributes["PRE_DIR"] == null ? "" : feature.attributes["PRE_DIR"] + " ") + feature.attributes["FULL_STNAME"] + " " + (feature.attributes["SUF_DIR"] == null ? "" : feature.attributes["SUF_DIR"]) + " " + feature.attributes["CITY"] + ", IN " + feature.attributes["ZIPCODE"];
        };
        LocationApp.prototype._parcelPopupContent = function (feature) {
            return "<b>Street Address: </b>" + feature.attributes["STNUMBER"] + " " + (feature.attributes["PRE_DIR"] == null ? "" : feature.attributes["PRE_DIR"] + " ") + feature.attributes["FULL_STNAME"] + " " + (feature.attributes["SUF_DIR"] == null ? "" : feature.attributes["SUF_DIR"] + " ") + "<br/>" +
                "<b>City, State Zip: </b>" + feature.attributes["CITY"] + ", IN " + feature.attributes["ZIPCODE"];
        };
        LocationApp.prototype._cleanUpResults = function () {
            // Clear the lookup results displayed in the side panel
            this.lookupResults && this.lookupResults.clearResults();
            this._results = null;
            this.view.graphics.removeAll();
            this.mapPanel && this.mapPanel.clearResults();
        };
        LocationApp.prototype._updateUrlParam = function (searchTerm, index) {
            if ('URLSearchParams' in window) {
                var params = new URLSearchParams(document.location.search);
                if (searchTerm) {
                    if (index && (index > 0 || index === 0)) {
                        params.set('findSource', index);
                    }
                    else {
                        params.delete('findSource');
                    }
                    params.set('find', encodeURIComponent(this.searchWidget.searchTerm));
                }
                else {
                    params.delete('find');
                    params.delete('findSource');
                }
                window.history.replaceState({}, '', location.pathname + "?" + params);
            }
        };
        LocationApp.prototype._createSharedTheme = function () {
            var _a, _b, _c;
            // use shared theme colors for header and buttons 
            var sharedTheme = (_c = (_b = (_a = this.base) === null || _a === void 0 ? void 0 : _a.portal) === null || _b === void 0 ? void 0 : _b.portalProperties) === null || _c === void 0 ? void 0 : _c.sharedTheme;
            if (!sharedTheme) {
                return;
            }
            var header = sharedTheme.header, button = sharedTheme.button;
            var styles = [];
            // Build and insert style
            if (header === null || header === void 0 ? void 0 : header.background)
                styles.push(".shared-theme .app-header{\n\t\t\tbackground:" + header.background + ";\n\t\t}\n\t\t.shared-theme .text-fade:after {\n\t\t\tbackground: linear-gradient(to left, " + header.background + ", 40%, transparent 100%);\n\t\t  }\n\t\t  html[dir=\"rtl\"] .light .text-fade:after {\n\t\t\tbackground: linear-gradient(to right, " + header.background + ", 40%, transparent 100%);\n\t\t  }\n\t\t");
            if (header === null || header === void 0 ? void 0 : header.text)
                styles.push(".shared-theme .app-header a{\n\t\t\tcolor:" + header.text + ";\n\t\t} \n\t\t.shared-theme .app-header{\n\t\t\tcolor:" + header.text + ";\n\t\t} \n\t\t.shared-theme .toolbar-buttons{color:" + header.text + "}");
            if (button === null || button === void 0 ? void 0 : button.background)
                styles.push(".shared-theme .app-button{\n\t\t\tbackground:" + button.background + ";\n\t\t\t border-color:" + button.background + ";\n\t\t\t}\n\t\t\t.shared-theme .app-button.btn-blue{\n\t\t\t\tbackground:" + button.background + ";\n\t\t\t\tborder-color:" + button.background + ";\n\t\t\t} \n\t\t\t.shared-theme #detailPanel .svg-icon{\n\t\t\t\tcolor:" + button.background + ";}\n\t\t\t }");
            if (button === null || button === void 0 ? void 0 : button.text)
                styles.push(".shared-theme .app-button{\n\t\t\t\tcolor:" + button.text + ";\n\t\t\t}\n\t\t\t.shared-theme .app-button.btn-blue{color:" + button.text + ";}\n\t\t};");
            if (styles && styles.length && styles.length > 0) {
                var style = document.createElement('style');
                style.appendChild(document.createTextNode(styles.join('')));
                document.getElementsByTagName('head')[0].appendChild(style);
            }
        };
        LocationApp.prototype.handleThemeUpdates = function () {
            // Check for a preferred color scheme and then
            // monitor updates to that color scheme and the
            // configuration panel updates.
            var _a = this._appConfig, theme = _a.theme, applySharedTheme = _a.applySharedTheme;
            if (theme) {
                var style = document.getElementById("esri-stylesheet");
                style.href = style.href.indexOf("light") !== -1 ? style.href.replace(/light/g, theme) : style.href.replace(/dark/g, theme);
                // add light/dark class
                document.body.classList.add(theme === "light" ? "light" : "dark");
                document.body.classList.remove(theme === "light" ? "dark" : "light");
            }
            applySharedTheme ? document.body.classList.add("shared-theme") : document.body.classList.remove("shared-theme");
        };
        LocationApp.prototype._cleanUpHandles = function () {
            // if we aren't in the config experience remove all handlers after load
            if (!this._appConfig.withinConfigurationExperience) {
                this._handles.remove("configuration");
            }
        };
        return LocationApp;
    }());
    return LocationApp;
});
//# sourceMappingURL=Main.js.map